import { useState, useCallback, memo, useMemo, useEffect } from 'react';
import { CancelBookingConfirmation } from './CancelBookingConfirmation';
import { __, sprintf } from '@wordpress/i18n';
import {
	formatDuration,
	formatFullDate,
	formatTime,
} from '../../fields/utils/date-time';
import { addMinutes } from 'date-fns';
import { LazyMotion } from 'motion/react';
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	SelectControl,
	Spinner,
} from '@wordpress/components';
import { useManagement } from '../context/ManagementContext';
import type { Booking } from '../../typings/common/booking';
import { BookingTimePicker } from '../../fields/components/BookingTimePicker';
import { useBookingStore } from '../../fields/store/useBookingStore';
import {
	useBookingTimeValue,
	useService,
} from '../../fields/hooks/useBookingComputed';
import { useServiceResourcesQuery } from '../../fields/queries';
import { TimezoneProvider } from '../../shared/contexts/TimezoneContext';
import { getStatusLabel } from '../../helpers/booking';

interface Props {
	booking: Booking;
	resources: Booking[];
}

export const BookingCard = memo(({ booking, resources }: Props) => {
	const { rescheduleBooking, cancelBooking } = useManagement();
	const [isRescheduling, setIsRescheduling] = useState(false);
	const [showCancelConfirm, setShowCancelConfirm] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Cancel booking confirmation flow
	const handleCancelBooking = useCallback(async () => {
		setIsSubmitting(true);
		setError(null);
		try {
			await cancelBooking(booking.id);
			setShowCancelConfirm(false);
		} catch (err) {
			const apiError = err as { message?: string };
			setError(
				apiError.message ||
					__(
						'Failed to cancel booking. Please try again.',
						'gp-bookings'
					)
			);
		} finally {
			setIsSubmitting(false);
		}
	}, [booking.id, cancelBooking]);

	const setConfig = useBookingStore((state) => state.setConfig);
	const setCurrentServiceId = useBookingStore(
		(state) => state.setCurrentServiceId
	);
	const setSelectedResourceIds = useBookingStore(
		(state) => state.setSelectedResourceIds
	);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const setExcludeBookingId = useBookingStore(
		(state) => state.setExcludeBookingId
	);

	// Get computed booking time value from Zustand store
	const bookingTimeValue = useBookingTimeValue();
	const { data: service } = useService();

	const resourceIds = useMemo(() => {
		return resources.map((resource) => resource.objectId);
	}, [resources]);

	const currentResourceId =
		resources.length === 1 ? resources[0].objectId : null;
	const canReselectResource =
		Boolean(booking.canChangeResource) && resources.length === 1;
	const selectedResourceId =
		selectedResourceIds.length > 0 ? selectedResourceIds[0] : null;

	const {
		data: serviceResources = [],
		isLoading: isServiceResourcesLoading,
	} = useServiceResourcesQuery(
		{ id: booking.serviceId },
		{
			enabled: isRescheduling && canReselectResource,
		}
	);

	const hasResourceChanged = useMemo(() => {
		return (
			canReselectResource &&
			currentResourceId !== null &&
			selectedResourceId !== null &&
			selectedResourceId !== currentResourceId
		);
	}, [canReselectResource, currentResourceId, selectedResourceId]);

	// Check if the selected time is different from the current booking time
	const hasTimeChanged = useMemo(() => {
		if (!bookingTimeValue || !service) {
			return false;
		}

		// Parse the selected value
		const [selectedStart, selectedEnd] = bookingTimeValue.includes('|')
			? bookingTimeValue.split('|')
			: [bookingTimeValue, null];

		// Parse and normalize all datetime strings to Date objects for comparison
		const selectedStartDate = new Date(selectedStart.replace(' ', 'T'));
		const bookingStartDate = new Date(
			(typeof booking.start === 'string'
				? booking.start
				: booking.start.toISOString()
			).replace(' ', 'T')
		);

		// Calculate or parse the end time
		const selectedEndDate = selectedEnd
			? new Date(selectedEnd.replace(' ', 'T'))
			: addMinutes(selectedStartDate, service.blockSize);

		const bookingEndDate = new Date(
			(typeof booking.end === 'string'
				? booking.end
				: booking.end.toISOString()
			).replace(' ', 'T')
		);

		// Compare timestamps
		return (
			selectedStartDate.getTime() !== bookingStartDate.getTime() ||
			selectedEndDate.getTime() !== bookingEndDate.getTime()
		);
	}, [bookingTimeValue, service, booking.start, booking.end]);

	// Initialize Zustand store when entering reschedule mode
	useEffect(() => {
		if (isRescheduling) {
			setConfig({
				formId: 0,
				bookingFieldId: 0,
				serviceFieldId: null,
				resourceFieldIds: [],
				slotMode: 'aligned',
				interval: 0,
				showEndTimes: true,
				showTimezoneIndicator: true,
				timezoneMessage: null,
				showSpotsLeft: false,
				spotsLeftText: null,
				showUserTimezone: false,
				showTwoMonths: false,
				startingMonthMode: 'current',
				startingMonth: null,
				startingYear: null,
			});
			setCurrentServiceId(booking.serviceId);
			setSelectedResourceIds(resourceIds);
			setExcludeBookingId(booking.id);
		} else {
			// Clear excludeBookingId when exiting reschedule mode
			setExcludeBookingId(null);
		}
	}, [
		isRescheduling,
		booking.serviceId,
		booking.id,
		resourceIds,
		setConfig,
		setCurrentServiceId,
		setSelectedResourceIds,
		setExcludeBookingId,
	]);

	const handleReschedule = useCallback(async () => {
		if (!bookingTimeValue) {
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const resourceIdsForSubmit =
				canReselectResource &&
				selectedResourceId !== null &&
				selectedResourceId > 0
					? [selectedResourceId]
					: undefined;

			if (bookingTimeValue.includes('|')) {
				const [start, end] = bookingTimeValue.split('|');
				await rescheduleBooking(
					booking.id,
					start,
					end,
					resourceIdsForSubmit
				);
			} else {
				await rescheduleBooking(
					booking.id,
					bookingTimeValue,
					undefined,
					resourceIdsForSubmit
				);
			}
			setIsRescheduling(false);
		} catch (err) {
			const apiError = err as { message?: string };
			setError(
				apiError.message ||
					__(
						'Failed to reschedule booking. Please try again.',
						'gp-bookings'
					)
			);
		} finally {
			setIsSubmitting(false);
		}
	}, [
		booking.id,
		rescheduleBooking,
		bookingTimeValue,
		canReselectResource,
		selectedResourceId,
	]);

	return (
		<Card className="gpb-manage-booking">
			<CardHeader>
				<div className="gpb-manage-booking-header">
					<h3>{booking.objectTitle}</h3>
					{booking.customerName && (
						<div className="gpb-manage-booking-customer">
							{booking.customerName}
						</div>
					)}
					<div className="gpb-manage-booking-status">
						{__('Status:', 'gp-bookings')}{' '}
						{getStatusLabel(booking.status)}
					</div>
				</div>
				{resources.length > 0 && (
					<div className="gpb-manage-booking-resources">
						{resources.map((resource) => (
							<div
								key={resource.id}
								className="gpb-manage-booking-resource"
							>
								{resource.objectTitle}
							</div>
						))}
					</div>
				)}
			</CardHeader>
			<CardBody>
				<div className="gpb-manage-booking-times">
					<div>
						{__('Start:', 'gp-bookings')}{' '}
						{formatFullDate(new Date(booking.start))}{' '}
						{formatTime(new Date(booking.start))}
					</div>
					<div>
						{__('End:', 'gp-bookings')}{' '}
						{formatFullDate(new Date(booking.end))}{' '}
						{formatTime(new Date(booking.end))}
					</div>
				</div>

				{isRescheduling && (
					<div className="gpb-manage-booking-reschedule">
						{canReselectResource && (
							<div className="gpb-manage-booking-resource-selector">
								<SelectControl
									label={__('Resource', 'gp-bookings')}
									value={
										selectedResourceId !== null
											? String(selectedResourceId)
											: String(currentResourceId ?? '')
									}
									options={serviceResources.map(
										(resource) => ({
											label: resource.name,
											value: String(resource.id),
										})
									)}
									onChange={(value) => {
										const parsedValue = parseInt(value, 10);
										setSelectedResourceIds(
											Number.isNaN(parsedValue)
												? []
												: [parsedValue]
										);
									}}
									disabled={
										isSubmitting ||
										isServiceResourcesLoading ||
										serviceResources.length === 0
									}
								/>
							</div>
						)}

						<LazyMotion
							features={() =>
								import('../lib/motion-features').then(
									(res) => res.domAnimation
								)
							}
						>
							<TimezoneProvider>
								<BookingTimePicker
									value={`${booking.start}|${booking.end}`}
								/>
							</TimezoneProvider>
						</LazyMotion>

						{error && (
							<div className="gpb-manage-booking-reschedule-error">
								{error}
							</div>
						)}

						<div className="gpb-manage-booking-reschedule-actions">
							<Button
								variant="secondary"
								onClick={() => setIsRescheduling(false)}
								disabled={isSubmitting}
							>
								{__('Keep current time', 'gp-bookings')}
							</Button>
							<Button
								variant="primary"
								onClick={handleReschedule}
								disabled={
									!bookingTimeValue ||
									(!hasTimeChanged && !hasResourceChanged) ||
									isSubmitting
								}
							>
								{isSubmitting ? (
									<>
										<Spinner />
										{__('Saving…', 'gp-bookings')}
									</>
								) : (
									__('Save new time', 'gp-bookings')
								)}
							</Button>
						</div>
					</div>
				)}

				{showCancelConfirm && (
					<CancelBookingConfirmation
						onConfirm={handleCancelBooking}
						onCancel={() => {
							setShowCancelConfirm(false);
							setError(null);
						}}
						isSubmitting={isSubmitting}
						error={error}
					/>
				)}

				{(booking.managementRestrictions?.reschedule ||
					booking.managementRestrictions?.cancel) && (
					<div className="gpb-manage-booking-restrictions">
						{(() => {
							const reschedule =
								booking.managementRestrictions?.reschedule;
							const cancel =
								booking.managementRestrictions?.cancel;

							if (
								reschedule?.type === 'not_allowed' &&
								cancel?.type === 'not_allowed'
							) {
								return __(
									'Rescheduling and cancellation are not allowed for this service.',
									'gp-bookings'
								);
							}

							if (
								reschedule?.type === 'deadline' &&
								cancel?.type === 'deadline' &&
								typeof reschedule.deadline === 'number' &&
								typeof cancel.deadline === 'number'
							) {
								if (reschedule.deadline === cancel.deadline) {
									return sprintf(
										/* translators: %s: time before booking (e.g. "2 hours" or "1 day") */
										__(
											'Changes must be made at least %s before the booking.',
											'gp-bookings'
										),
										formatDuration(reschedule.deadline * 60)
									);
								}

								return sprintf(
									/* translators: 1: time before booking for reschedule, 2: time before booking for cancel */
									__(
										'Rescheduling must be done at least %1$s before the booking. Cancellation must be done at least %2$s before the booking.',
										'gp-bookings'
									),
									formatDuration(reschedule.deadline * 60),
									formatDuration(cancel.deadline * 60)
								);
							}

							const messages = [];

							if (reschedule) {
								if (reschedule.type === 'not_allowed') {
									messages.push(
										__(
											'This booking cannot be rescheduled.',
											'gp-bookings'
										)
									);
								} else if (
									reschedule.type === 'deadline' &&
									typeof reschedule.deadline === 'number'
								) {
									messages.push(
										sprintf(
											/* translators: %s: time before booking (e.g. "2 hours" or "1 day") */
											__(
												'Rescheduling must be done at least %s before the booking.',
												'gp-bookings'
											),
											formatDuration(
												reschedule.deadline * 60
											)
										)
									);
								}
							}

							if (cancel) {
								if (cancel.type === 'not_allowed') {
									messages.push(
										__(
											'This booking cannot be canceled.',
											'gp-bookings'
										)
									);
								} else if (
									cancel.type === 'deadline' &&
									typeof cancel.deadline === 'number'
								) {
									messages.push(
										sprintf(
											/* translators: %s: time before booking (e.g. "2 hours" or "1 day") */
											__(
												'Cancellation must be done at least %s before the booking.',
												'gp-bookings'
											),
											formatDuration(cancel.deadline * 60)
										)
									);
								}
							}

							return messages.join(' ');
						})()}
					</div>
				)}

				<div className="gpb-manage-booking-actions">
					{!isRescheduling &&
						!showCancelConfirm &&
						!['cancelled', 'completed'].includes(
							booking.status
						) && (
							<>
								{booking.canReschedule && (
									<Button
										variant="secondary"
										onClick={() => setIsRescheduling(true)}
									>
										{__('Reschedule', 'gp-bookings')}
									</Button>
								)}
								{booking.canCancel && (
									<Button
										variant="secondary"
										isDestructive
										onClick={() =>
											setShowCancelConfirm(true)
										}
									>
										{__('Cancel', 'gp-bookings')}
									</Button>
								)}
							</>
						)}
				</div>
			</CardBody>
		</Card>
	);
});
